import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  Plus, Wallet, HandCoins, Clock, CheckCircle, XCircle,
  ArrowDownCircle, TrendingUp, CircleDollarSign, Users, Ban,
  ShieldCheck, UserCheck, AlertTriangle,
} from "lucide-react";
import api from "../lib/api";

const statusStyles = {
  pending_dept_head: "bg-amber-50 text-amber-700 border-amber-200",
  pending_manager: "bg-amber-50 text-amber-700 border-amber-200",
  pending_hr: "bg-orange-50 text-orange-700 border-orange-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  rejected_by_dept: "bg-orange-100 text-orange-800 border-orange-300",
  approved: "bg-green-50 text-green-700 border-green-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  completed: "bg-blue-50 text-blue-700 border-blue-200",
};

const statusLabels = {
  pending_dept_head: "Pending Dept Approval",
  pending_manager: "Pending Manager",
  pending_hr: "Pending HR",
  pending: "Pending",
  rejected_by_dept: "Dept Rejected (HR Can Override)",
  approved: "Approved",
  rejected: "Rejected",
  completed: "Completed",
};

const PENDING_STATUSES = ["pending_dept_head", "pending_manager", "pending_hr", "pending", "rejected_by_dept"];

const AdvancesLoans = () => {
  const { currentBusiness } = useAuth();
  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState({});
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [repayOpen, setRepayOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [creating, setCreating] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const [checkingElig, setCheckingElig] = useState(false);

  const [form, setForm] = useState({
    employee_id: "", type: "advance", amount: "", reason: "", tenure_months: "", emi_amount: "",
  });
  const [approveForm, setApproveForm] = useState({ remarks: "", emi_amount: "", tenure_months: "" });
  const [repayForm, setRepayForm] = useState({ amount: "", note: "" });

  const fetchData = useCallback(async () => {
    if (!currentBusiness) return;
    try {
      const [recRes, statsRes, empRes] = await Promise.all([
        api.get(`/advances?business_id=${currentBusiness.id}`),
        api.get(`/advances/stats?business_id=${currentBusiness.id}`),
        api.get(`/employees?business_id=${currentBusiness.id}`),
      ]);
      setRecords(recRes.data);
      setStats(statsRes.data);
      const list = Array.isArray(empRes.data) ? empRes.data : empRes.data.employees || [];
      setEmployees(list.filter(e => e.is_active !== false));
    } catch { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  }, [currentBusiness]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Check eligibility when employee/type changes
  const checkEligibility = useCallback(async (empId, type) => {
    if (!empId || !currentBusiness) { setEligibility(null); return; }
    setCheckingElig(true);
    try {
      const res = await api.get(`/advances/eligibility/${empId}?request_type=${type}&business_id=${currentBusiness.id}`);
      setEligibility(res.data);
    } catch { setEligibility(null); }
    finally { setCheckingElig(false); }
  }, [currentBusiness]);

  useEffect(() => {
    if (form.employee_id && form.type) checkEligibility(form.employee_id, form.type);
  }, [form.employee_id, form.type, checkEligibility]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.employee_id || !form.amount) { toast.error("Employee and amount are required"); return; }
    setCreating(true);
    try {
      await api.post("/advances", { ...form, business_id: currentBusiness.id, amount: parseFloat(form.amount) });
      toast.success("Request created");
      setCreateOpen(false);
      setForm({ employee_id: "", type: "advance", amount: "", reason: "", tenure_months: "", emi_amount: "" });
      setEligibility(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create"); }
    finally { setCreating(false); }
  };

  const handleApprove = async () => {
    try {
      const res = await api.put(`/advances/${selectedRecord.id}/approve`, approveForm);
      toast.success(res.data.message || "Approved");
      setApproveOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleReject = async (rec) => {
    const remarks = window.prompt("Rejection reason (optional):");
    if (remarks === null) return;
    try {
      await api.put(`/advances/${rec.id}/reject`, { remarks });
      toast.success("Rejected");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleRepayment = async (e) => {
    e.preventDefault();
    if (!repayForm.amount || parseFloat(repayForm.amount) <= 0) { toast.error("Enter valid amount"); return; }
    try {
      await api.post(`/advances/${selectedRecord.id}/repayment`, {
        amount: parseFloat(repayForm.amount), note: repayForm.note,
      });
      toast.success("Repayment recorded");
      setRepayOpen(false);
      setRepayForm({ amount: "", note: "" });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const openApproveDialog = (rec) => {
    setSelectedRecord(rec);
    setApproveForm({ remarks: "", emi_amount: rec.emi_amount || "", tenure_months: rec.tenure_months || "" });
    setApproveOpen(true);
  };

  const openRepayDialog = (rec) => {
    setSelectedRecord(rec);
    setRepayForm({ amount: rec.emi_amount || "", note: "" });
    setRepayOpen(true);
  };

  const formatCurrency = (v) => `${parseFloat(v || 0).toLocaleString("en-IN")}`;
  const formatDate = (iso) => new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

  const pending = records.filter(r => PENDING_STATUSES.includes(r.status));
  const active = records.filter(r => r.status === "approved");
  const closed = records.filter(r => ["rejected", "completed"].includes(r.status));

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-10 w-10 border-4 border-primary border-t-transparent" /></div>;
  }

  const ApprovalChain = ({ chain, status }) => {
    const getNextStepLabel = () => {
      if (status === "pending_dept_head" || status === "pending_manager") return "Dept/Manager";
      if (status === "pending_hr") return "HR";
      if (status === "rejected_by_dept") return "HR (Override)";
      return "Approval";
    };
    
    if (!chain || chain.length === 0) {
      return (
        <div className="flex items-center gap-2 mt-2 text-xs">
          <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full ${["pending_dept_head", "pending_manager"].includes(status) ? "bg-amber-100 text-amber-700 font-medium" : "bg-slate-100 text-slate-400"}`}>
            <UserCheck className="h-3 w-3" /> Dept
          </div>
          <span className="text-slate-300">→</span>
          <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full ${status === "pending_hr" ? "bg-orange-100 text-orange-700 font-medium" : status === "rejected_by_dept" ? "bg-violet-100 text-violet-700 font-medium" : "bg-slate-100 text-slate-400"}`}>
            <ShieldCheck className="h-3 w-3" /> HR
          </div>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-2 mt-2 text-xs flex-wrap">
        {chain.map((step, i) => (
          <div key={`approval-step-${step.date || i}-${step.approver_name || i}`} className={`flex items-center gap-1 px-2 py-0.5 rounded-full ${step.action === "approved" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
            {step.action === "approved" ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
            {step.approver_name || step.approver_role}
          </div>
        ))}
        {PENDING_STATUSES.includes(status) && (
          <>
            <span className="text-slate-300">→</span>
            <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full ${status === "rejected_by_dept" ? "bg-violet-100 text-violet-700 font-medium" : status === "pending_hr" ? "bg-orange-100 text-orange-700 font-medium" : "bg-amber-100 text-amber-700 font-medium"}`}>
              <Clock className="h-3 w-3" /> {getNextStepLabel()}
            </div>
          </>
        )}
      </div>
    );
  };

  const RecordCard = ({ rec }) => {
    const progress = rec.amount > 0 ? Math.min(100, ((rec.total_repaid || 0) / rec.amount) * 100) : 0;
    const isPending = PENDING_STATUSES.includes(rec.status);
    return (
      <Card className="card-soft hover:shadow-md transition-shadow" data-testid={`advance-${rec.id}`}>
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <Badge variant="outline" className={rec.type === "loan" ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "bg-teal-50 text-teal-700 border-teal-200"}>
                  {rec.type === "loan" ? <HandCoins className="h-3 w-3 mr-1" /> : <Wallet className="h-3 w-3 mr-1" />}
                  {rec.type === "loan" ? "Loan" : "Advance"}
                </Badge>
                <Badge variant="outline" className={statusStyles[rec.status] || statusStyles.pending}>
                  {statusLabels[rec.status] || rec.status}
                </Badge>
              </div>
              <p className="font-semibold text-slate-800 text-lg">{formatCurrency(rec.amount)}</p>
              <p className="text-sm text-slate-600 mt-0.5">{rec.employee_name}</p>
              {rec.reason && <p className="text-xs text-slate-500 mt-1">{rec.reason}</p>}

              {/* Approval Chain */}
              {isPending && <ApprovalChain chain={rec.approval_chain} status={rec.status} />}

              {rec.status === "approved" && (
                <div className="mt-3 space-y-1.5">
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>Repaid: {formatCurrency(rec.total_repaid)}</span>
                    <span>Remaining: {formatCurrency(rec.amount - (rec.total_repaid || 0))}</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div className="bg-green-500 h-2 rounded-full transition-all" style={{ width: `${progress}%` }} data-testid={`repay-progress-${rec.id}`} />
                  </div>
                  {rec.emi_amount && <p className="text-xs text-slate-400">EMI: {formatCurrency(rec.emi_amount)}/month{rec.tenure_months ? ` | ${rec.tenure_months} months` : ""}</p>}
                </div>
              )}
              {rec.admin_remarks && <p className="text-xs text-slate-400 mt-2 italic">Remarks: {rec.admin_remarks}</p>}
              <p className="text-xs text-slate-300 mt-2">{formatDate(rec.created_at)}</p>
            </div>
            <div className="flex flex-col gap-2">
              {isPending && (
                <>
                  <Button size="sm" className="btn-primary text-xs" onClick={() => openApproveDialog(rec)} data-testid={`approve-${rec.id}`}>
                    <CheckCircle className="h-3 w-3 mr-1" />
                    {rec.status === "pending_dept_head" || rec.status === "pending_manager" ? "Dept Approve" : 
                     rec.status === "rejected_by_dept" ? "HR Override" : "HR Approve"}
                  </Button>
                  {rec.status !== "rejected_by_dept" && (
                    <Button size="sm" variant="outline" className="text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleReject(rec)} data-testid={`reject-${rec.id}`}>
                      <Ban className="h-3 w-3 mr-1" /> Reject
                    </Button>
                  )}
                </>
              )}
              {rec.status === "approved" && (
                <Button size="sm" variant="outline" className="text-xs" onClick={() => openRepayDialog(rec)} data-testid={`record-repay-${rec.id}`}>
                  <ArrowDownCircle className="h-3 w-3 mr-1" /> Repayment
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6" data-testid="advances-loans-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-heading">Advances & Loans</h1>
          <p className="text-slate-500">Track salary advances and employee loans</p>
        </div>
        <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) { setEligibility(null); setForm({ employee_id: "", type: "advance", amount: "", reason: "", tenure_months: "", emi_amount: "" }); } }}>
          <DialogTrigger asChild>
            <Button className="btn-primary gap-2" data-testid="create-advance-btn"><Plus className="h-4 w-4" /> New Request</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2"><Wallet className="h-5 w-5 text-primary" /> Create Request</DialogTitle>
              <DialogDescription>Create an advance or loan request for an employee</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label>Employee</Label>
                <Select value={form.employee_id} onValueChange={v => setForm({ ...form, employee_id: v })}>
                  <SelectTrigger className="input-field" data-testid="advance-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent>
                    {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                    <SelectTrigger className="input-field" data-testid="advance-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="advance">Salary Advance</SelectItem>
                      <SelectItem value="loan">Loan</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Amount {eligibility?.max_amount > 0 && <span className="text-xs text-slate-400">(max {formatCurrency(eligibility.max_amount)})</span>}</Label>
                  <Input type="number" min="1" max={eligibility?.max_amount || undefined} value={form.amount}
                    onChange={e => setForm({ ...form, amount: e.target.value })}
                    placeholder={eligibility?.max_amount ? `Max ${formatCurrency(eligibility.max_amount)}` : "Amount"}
                    className="input-field" required data-testid="advance-amount-input" />
                </div>
              </div>

              {/* Eligibility Info */}
              {form.employee_id && eligibility && (
                <div className={`p-3 rounded-lg border text-sm ${eligibility.eligible ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`} data-testid="eligibility-box">
                  {eligibility.eligible ? (
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle className="h-4 w-4 flex-shrink-0" />
                      <span>Eligible — Max: {formatCurrency(eligibility.max_amount)}</span>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center gap-2 text-red-700 font-medium mb-1">
                        <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                        Not Eligible
                      </div>
                      <ul className="text-red-600 text-xs space-y-0.5 ml-6 list-disc">
                        {eligibility.reasons.map((r, i) => <li key={`elig-reason-${i}-${r.substring(0, 10)}`}>{r}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              {checkingElig && form.employee_id && (
                <p className="text-xs text-slate-400">Checking eligibility...</p>
              )}

              {form.type === "loan" && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tenure (months)</Label>
                    <Input type="number" min="1" value={form.tenure_months} onChange={e => setForm({ ...form, tenure_months: e.target.value })}
                      placeholder="12" className="input-field" data-testid="advance-tenure-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>EMI Amount</Label>
                    <Input type="number" min="1" value={form.emi_amount} onChange={e => setForm({ ...form, emi_amount: e.target.value })}
                      placeholder="Auto" className="input-field" data-testid="advance-emi-input" />
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label>Reason</Label>
                <Textarea value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })}
                  placeholder="Reason for the request..." className="input-field min-h-[70px]" data-testid="advance-reason-input" />
              </div>
              <Button type="submit" className="w-full btn-primary" disabled={creating || (eligibility && !eligibility.eligible)} data-testid="submit-advance-btn">
                {creating ? "Creating..." : "Submit Request"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="stat-card">
          <CardContent className="p-0">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-amber-100"><Clock className="h-6 w-6 text-amber-700" /></div>
              <div><p className="text-2xl font-bold text-slate-800">{stats.pending_count || 0}</p><p className="text-sm text-slate-500">Pending</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-0">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-green-100"><TrendingUp className="h-6 w-6 text-green-700" /></div>
              <div><p className="text-2xl font-bold text-slate-800">{formatCurrency(stats.total_disbursed)}</p><p className="text-sm text-slate-500">Disbursed</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-0">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-red-100"><CircleDollarSign className="h-6 w-6 text-red-700" /></div>
              <div><p className="text-2xl font-bold text-slate-800">{formatCurrency(stats.outstanding_balance)}</p><p className="text-sm text-slate-500">Outstanding</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-0">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-blue-100"><Users className="h-6 w-6 text-blue-700" /></div>
              <div><p className="text-2xl font-bold text-slate-800">{stats.active_count || 0}</p><p className="text-sm text-slate-500">Active</p></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="pending" className="space-y-4">
        <TabsList className="bg-white border border-slate-200 p-1 rounded-full">
          <TabsTrigger value="pending" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="pending-tab">
            Pending ({pending.length})
          </TabsTrigger>
          <TabsTrigger value="active" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="active-tab">
            Active ({active.length})
          </TabsTrigger>
          <TabsTrigger value="closed" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="closed-tab">
            Closed ({closed.length})
          </TabsTrigger>
        </TabsList>

        {["pending", "active", "closed"].map(tab => (
          <TabsContent key={tab} value={tab}>
            {(tab === "pending" ? pending : tab === "active" ? active : closed).length === 0 ? (
              <Card className="card-soft"><CardContent className="py-12 text-center text-slate-500">
                No {tab} requests
              </CardContent></Card>
            ) : (
              <div className="space-y-3">{(tab === "pending" ? pending : tab === "active" ? active : closed).map(r => <RecordCard key={r.id} rec={r} />)}</div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Approve Dialog */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><CheckCircle className="h-5 w-5 text-green-600" /> Approve Request</DialogTitle>
            <DialogDescription>
              {selectedRecord?.status === "pending_dept_head" || selectedRecord?.status === "pending_manager" 
                ? "Department approval — will forward to HR next" 
                : selectedRecord?.status === "rejected_by_dept"
                ? "HR Override — department rejected but you can approve"
                : "Final HR approval"}
            </DialogDescription>
          </DialogHeader>
          {selectedRecord && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-slate-50 text-sm">
                <p className="font-medium text-slate-800">{selectedRecord.employee_name} — {formatCurrency(selectedRecord.amount)}</p>
                <p className="text-slate-500 text-xs">{selectedRecord.type === "loan" ? "Loan" : "Salary Advance"}</p>
              </div>
              {selectedRecord.status === "rejected_by_dept" && (
                <div className="p-2 bg-orange-50 border border-orange-200 rounded text-xs text-orange-700">
                  Note: Department approver rejected this request. You can override and approve.
                </div>
              )}
              {selectedRecord.type === "loan" && (selectedRecord.status === "pending_hr" || selectedRecord.status === "rejected_by_dept") && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Tenure (months)</Label>
                    <Input type="number" min="1" value={approveForm.tenure_months} onChange={e => setApproveForm({ ...approveForm, tenure_months: e.target.value })}
                      className="input-field" data-testid="approve-tenure-input" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">EMI Amount</Label>
                    <Input type="number" min="1" value={approveForm.emi_amount} onChange={e => setApproveForm({ ...approveForm, emi_amount: e.target.value })}
                      className="input-field" data-testid="approve-emi-input" />
                  </div>
                </div>
              )}
              <div className="space-y-1">
                <Label className="text-xs">Remarks (optional)</Label>
                <Textarea value={approveForm.remarks} onChange={e => setApproveForm({ ...approveForm, remarks: e.target.value })}
                  className="input-field min-h-[60px]" placeholder="Any notes..." data-testid="approve-remarks-input" />
              </div>
              <Button className="w-full btn-primary" onClick={handleApprove} data-testid="confirm-approve-btn">
                {selectedRecord.status === "pending_dept_head" || selectedRecord.status === "pending_manager" 
                  ? "Approve & Forward to HR" 
                  : selectedRecord.status === "rejected_by_dept"
                  ? "Override & Approve"
                  : "Give Final Approval"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Repayment Dialog */}
      <Dialog open={repayOpen} onOpenChange={setRepayOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><ArrowDownCircle className="h-5 w-5 text-primary" /> Record Repayment</DialogTitle>
            <DialogDescription>Record a repayment against this advance/loan</DialogDescription>
          </DialogHeader>
          {selectedRecord && (
            <form onSubmit={handleRepayment} className="space-y-4">
              <div className="p-3 rounded-lg bg-slate-50 text-sm">
                <p className="font-medium text-slate-800">{selectedRecord.employee_name}</p>
                <p className="text-slate-500 text-xs">Outstanding: {formatCurrency(selectedRecord.amount - (selectedRecord.total_repaid || 0))}</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Repayment Amount</Label>
                <Input type="number" min="1" value={repayForm.amount} onChange={e => setRepayForm({ ...repayForm, amount: e.target.value })}
                  className="input-field" required data-testid="repay-amount-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Note (optional)</Label>
                <Input value={repayForm.note} onChange={e => setRepayForm({ ...repayForm, note: e.target.value })}
                  className="input-field" placeholder="e.g. March salary deduction" data-testid="repay-note-input" />
              </div>
              <Button type="submit" className="w-full btn-primary" data-testid="confirm-repay-btn">Record Repayment</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdvancesLoans;
